import React, { useCallback, useEffect, useState, useRef } from 'react';
import {
  ReactFlow,
  Controls,
  Background,
  useNodesState,
  useEdgesState,
  addEdge,
  MiniMap,
  Panel,
  useReactFlow,
  ReactFlowProvider,
} from 'reactflow';
import {
  Box,
  Button,
  IconButton,
  Toolbar,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Fab,
  Tooltip,
  Paper,
  Divider,
  ButtonGroup,
  Menu,
  ListItemIcon,
  ListItemText,
  Alert,
  Snackbar
} from '@mui/material';
import {
  Add as AddIcon,
  Download as DownloadIcon,
  Upload as UploadIcon,
  Share as ShareIcon,
  ZoomIn as ZoomInIcon,
  ZoomOut as ZoomOutIcon,
  FitScreen as FitScreenIcon,
  AccountTree as DiagramIcon,
  Save as SaveIcon,
  Code as CodeIcon,
  Circle as CircleIcon,
  CropSquare as SquareIcon,
  PlayArrow as StartIcon,
  Stop as EndIcon,
  Transform as ProcessIcon,
  Help as DecisionIcon,
  Storage as DataIcon,
  CloudQueue as CloudIcon,
  Computer as SystemIcon,
  Person as UserIcon,
  Api as ApiIcon,
  Security as SecurityIcon,
  Build as BuildIcon,
  BugReport as TestIcon,
  Timeline as FlowIcon,
  AutoFixHigh as AutoIcon
} from '@mui/icons-material';
import dagre from 'dagre';
import mermaid from 'mermaid';

// Import React Flow styles
import 'reactflow/dist/style.css';

// Custom node types
import CustomNode from './nodes/CustomNode';
import ProcessNode from './nodes/ProcessNode';
import DecisionNode from './nodes/DecisionNode';
import StartEndNode from './nodes/StartEndNode';
import DataNode from './nodes/DataNode';

const nodeTypes = {
  custom: CustomNode,
  process: ProcessNode,
  decision: DecisionNode,
  startEnd: StartEndNode,
  data: DataNode,
};

const initialNodes = [];
const initialEdges = [];

// Node templates for different types
const nodeTemplates = {
  start: {
    type: 'startEnd',
    data: { label: 'Start', nodeType: 'start' },
    style: { background: '#10B981', color: 'white', border: 'none', borderRadius: '50px' }
  },
  end: {
    type: 'startEnd', 
    data: { label: 'End', nodeType: 'end' },
    style: { background: '#EF4444', color: 'white', border: 'none', borderRadius: '50px' }
  },
  process: {
    type: 'process',
    data: { label: 'Process', icon: ProcessIcon },
    style: { background: '#3B82F6', color: 'white', border: 'none', borderRadius: '8px' }
  },
  decision: {
    type: 'decision',
    data: { label: 'Decision', icon: DecisionIcon },
    style: { background: '#F59E0B', color: 'white', border: 'none', borderRadius: '8px' }
  },
  data: {
    type: 'data',
    data: { label: 'Data', icon: DataIcon },
    style: { background: '#8B5CF6', color: 'white', border: 'none', borderRadius: '8px' }
  },
  system: {
    type: 'custom',
    data: { label: 'System', icon: SystemIcon },
    style: { background: '#06B6D4', color: 'white', border: 'none', borderRadius: '8px' }
  },
  user: {
    type: 'custom',
    data: { label: 'User', icon: UserIcon },
    style: { background: '#84CC16', color: 'white', border: 'none', borderRadius: '8px' }
  },
  api: {
    type: 'custom',
    data: { label: 'API', icon: ApiIcon },
    style: { background: '#EC4899', color: 'white', border: 'none', borderRadius: '8px' }
  }
};

// Layout algorithms
const getLayoutedElements = (nodes, edges, direction = 'TB') => {
  const dagreGraph = new dagre.graphlib.Graph();
  dagreGraph.setDefaultEdgeLabel(() => ({}));
  
  const nodeWidth = 200;
  const nodeHeight = 60;
  
  const isHorizontal = direction === 'LR';
  dagreGraph.setGraph({ 
    rankdir: direction,
    nodesep: 100, // Horizontal spacing between nodes
    ranksep: 80,  // Vertical spacing between ranks
    marginx: 50,
    marginy: 50
  });
  
  nodes.forEach((node) => {
    dagreGraph.setNode(node.id, { width: nodeWidth, height: nodeHeight });
  });
  
  edges.forEach((edge) => {
    dagreGraph.setEdge(edge.source, edge.target);
  });
  
  dagre.layout(dagreGraph);
  
  nodes.forEach((node) => {
    const nodeWithPosition = dagreGraph.node(node.id);
    node.targetPosition = isHorizontal ? 'left' : 'top';
    node.sourcePosition = isHorizontal ? 'right' : 'bottom';
    
    // We are shifting the dagre node position (anchor=center center) to the top left
    // so it matches the React Flow node anchor point (top left).
    node.position = {
      x: nodeWithPosition.x - nodeWidth / 2,
      y: nodeWithPosition.y - nodeHeight / 2,
    };
    
    return node;
  });
  
  return { nodes, edges };
};

const FlowCanvasComponent = ({ initialMermaidCode, onSave, onCancel }) => {
  const [nodes, setNodes, onNodesChange] = useNodesState(initialNodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState(initialEdges);
  const [nodeMenu, setNodeMenu] = useState(null);
  const [selectedNodeType, setSelectedNodeType] = useState('process');
  const [nodeDialog, setNodeDialog] = useState(false);
  const [newNodeData, setNewNodeData] = useState({ label: '', description: '' });
  const [mermaidDialog, setMermaidDialog] = useState(false);
  const [mermaidCode, setMermaidCode] = useState('');
  const [notification, setNotification] = useState({ open: false, message: '', severity: 'info' });
  const [isLoading, setIsLoading] = useState(false);
  
  const reactFlowInstance = useReactFlow();
  const canvasRef = useRef(null);
  let nextNodeId = useRef(1);

  // Initialize Mermaid and load initial code
  useEffect(() => {
    mermaid.initialize({
      startOnLoad: false,
      theme: 'default',
      flowchart: {
        useMaxWidth: false,
        htmlLabels: true
      }
    });

    // Load initial Mermaid code if provided
    if (initialMermaidCode) {
      setMermaidCode(initialMermaidCode);
      // Auto-import the initial code
      setTimeout(() => {
        importFromMermaidCode(initialMermaidCode);
      }, 100);
    }
  }, [initialMermaidCode]);

  const onConnect = useCallback(
    (params) => setEdges((eds) => addEdge({ ...params, animated: true }, eds)),
    [setEdges]
  );

  const showNotification = (message, severity = 'info') => {
    setNotification({ open: true, message, severity });
  };

  // Add new node
  const addNode = (type, position = { x: 100, y: 100 }) => {
    const template = nodeTemplates[type] || nodeTemplates.process;
    const newNode = {
      id: `node_${nextNodeId.current++}`,
      position,
      ...template,
      data: {
        ...template.data,
        label: newNodeData.label || template.data.label,
        description: newNodeData.description
      }
    };
    
    setNodes((nds) => nds.concat(newNode));
    setNodeDialog(false);
    setNewNodeData({ label: '', description: '' });
    showNotification(`Added ${type} node`, 'success');
  };

  // Node menu handlers
  const handleNodeMenuOpen = (event, nodeType) => {
    setSelectedNodeType(nodeType);
    setNodeMenu(event.currentTarget);
  };

  const handleNodeMenuClose = () => {
    setNodeMenu(null);
  };

  const handleAddNodeClick = (type) => {
    setSelectedNodeType(type);
    setNodeDialog(true);
    handleNodeMenuClose();
  };

  // Auto-layout
  const onLayout = useCallback(
    (direction) => {
      const { nodes: layoutedNodes, edges: layoutedEdges } = getLayoutedElements(
        nodes,
        edges,
        direction
      );
      
      setNodes([...layoutedNodes]);
      setEdges([...layoutedEdges]);
      showNotification('Layout applied', 'success');
    },
    [nodes, edges, setNodes, setEdges]
  );

  // Export to JSON
  const exportFlow = () => {
    const flow = {
      nodes,
      edges,
      viewport: reactFlowInstance.getViewport()
    };
    
    const dataStr = JSON.stringify(flow, null, 2);
    const dataUri = 'data:application/json;charset=utf-8,'+ encodeURIComponent(dataStr);
    
    const exportFileDefaultName = 'flowchart.json';
    
    const linkElement = document.createElement('a');
    linkElement.setAttribute('href', dataUri);
    linkElement.setAttribute('download', exportFileDefaultName);
    linkElement.click();
    
    showNotification('Flowchart exported', 'success');
  };

  // Import from JSON
  const importFlow = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const flow = JSON.parse(e.target.result);
        if (flow.nodes) setNodes(flow.nodes);
        if (flow.edges) setEdges(flow.edges);
        if (flow.viewport) reactFlowInstance.setViewport(flow.viewport);
        
        showNotification('Flowchart imported successfully', 'success');
      } catch (error) {
        showNotification('Error importing flowchart', 'error');
      }
    };
    reader.readAsText(file);
  };

  // Export to Mermaid
  const exportToMermaid = () => {
    let mermaidStr = 'flowchart TD\n';
    
    nodes.forEach(node => {
      const shape = node.data.nodeType === 'start' || node.data.nodeType === 'end' ? 
        `((${node.data.label}))` :
        node.type === 'decision' ? `{${node.data.label}}` :
        `[${node.data.label}]`;
      
      mermaidStr += `    ${node.id}${shape}\n`;
    });
    
    edges.forEach(edge => {
      mermaidStr += `    ${edge.source} --> ${edge.target}\n`;
    });
    
    setMermaidCode(mermaidStr);
    setMermaidDialog(true);
  };

  // Import from Mermaid (with optional code parameter)
  const importFromMermaidCode = async (codeToImport) => {
    const code = codeToImport || mermaidCode;
    if (!code.trim()) return;
    
    setIsLoading(true);
    try {
      // Parse Mermaid code and convert to React Flow nodes/edges
      const lines = code.split('\n').filter(line => line.trim() && !line.includes('flowchart'));
      
      const nodeMap = new Map();
      const newNodes = [];
      const newEdges = [];
      let nodeCounter = 0;
      
      // First pass: collect all nodes and connections
      lines.forEach(line => {
        const trimmed = line.trim();
        
        // Parse connections first to identify all nodes
        if (trimmed.includes('-->')) {
          const parts = trimmed.split('-->');
          if (parts.length === 2) {
            const source = parts[0].trim();
            const target = parts[1].trim();
            
            // Extract node IDs from source and target
            const sourceId = source.split(/[\[\(\{]/)[0].trim();
            const targetId = target.split(/[\[\(\{]/)[0].trim();
            
            // Store node info if not already stored
            if (!nodeMap.has(sourceId)) {
              const sourceLabel = source.match(/[\[\(\{]([^[\]\)\}]+)[\]\)\}]/)?.[1] || sourceId;
              const sourceType = source.includes('((') ? 'start' :
                               source.includes('{') ? 'decision' : 'process';
              nodeMap.set(sourceId, { label: sourceLabel, type: sourceType });
            }
            
            if (!nodeMap.has(targetId)) {
              const targetLabel = target.match(/[\[\(\{]([^[\]\)\}]+)[\]\)\}]/)?.[1] || targetId;
              const targetType = target.includes('((') ? 'end' :
                               target.includes('{') ? 'decision' : 'process';
              nodeMap.set(targetId, { label: targetLabel, type: targetType });
            }
            
            // Create edge
            newEdges.push({
              id: `edge_${sourceId}_${targetId}`,
              source: sourceId,
              target: targetId,
              animated: true,
              type: 'smoothstep'
            });
          }
        }
        
        // Also parse standalone node definitions
        else if (trimmed.includes('[') || trimmed.includes('(') || trimmed.includes('{')) {
          const nodeId = trimmed.split(/[\[\(\{]/)[0].trim();
          if (!nodeMap.has(nodeId)) {
            const label = trimmed.match(/[\[\(\{]([^[\]\)\}]+)[\]\)\}]/)?.[1] || nodeId;
            const nodeType = trimmed.includes('((') ? 'start' :
                            trimmed.includes('{') ? 'decision' : 'process';
            nodeMap.set(nodeId, { label, type: nodeType });
          }
        }
      });
      
      // Create nodes with better positioning
      const nodeIds = Array.from(nodeMap.keys());
      nodeIds.forEach((nodeId, index) => {
        const nodeInfo = nodeMap.get(nodeId);
        const template = nodeTemplates[nodeInfo.type] || nodeTemplates.process;
        
        // Better positioning algorithm
        const col = index % 3; // 3 columns
        const row = Math.floor(index / 3);
        const x = 50 + col * 250; // 250px spacing between columns
        const y = 50 + row * 120; // 120px spacing between rows
        
        newNodes.push({
          id: nodeId,
          position: { x, y },
          ...template,
          data: { 
            ...template.data, 
            label: nodeInfo.label,
            description: `${nodeInfo.type} node`
          }
        });
      });
      
      if (newNodes.length > 0) {
        setNodes(newNodes);
        setEdges(newEdges);
        
        // Apply auto-layout for better visual organization
        setTimeout(() => {
          onLayout('TB');
        }, 100);
        
        showNotification(`Imported ${newNodes.length} nodes and ${newEdges.length} connections`, 'success');
        setMermaidDialog(false);
      } else {
        showNotification('No valid nodes found in Mermaid code', 'warning');
      }
    } catch (error) {
      console.error('Mermaid parsing error:', error);
      showNotification('Error parsing Mermaid code', 'error');
    }
    setIsLoading(false);
  };

  // Import from Mermaid
  const importFromMermaid = () => importFromMermaidCode();

  // Clear canvas
  const clearCanvas = () => {
    setNodes([]);
    setEdges([]);
    showNotification('Canvas cleared', 'info');
  };

  // Fit view
  const fitView = () => {
    reactFlowInstance.fitView();
  };

  return (
    <Box sx={{ width: '100%', height: '100vh', position: 'relative' }}>
      {/* Top Toolbar */}
      <Paper elevation={2} sx={{ position: 'absolute', top: 16, left: 16, right: 16, zIndex: 1000 }}>
        <Toolbar sx={{ gap: 2 }}>
          <Typography variant="h6" sx={{ flexGrow: 1 }}>
            Flow Canvas
          </Typography>
          
          {/* Node Creation */}
          <ButtonGroup variant="outlined" size="small">
            <Button
              startIcon={<AddIcon />}
              onClick={(e) => handleNodeMenuOpen(e, 'process')}
            >
              Add Node
            </Button>
            <Button onClick={(e) => handleNodeMenuOpen(e, 'process')}>
              ▼
            </Button>
          </ButtonGroup>
          
          <Divider orientation="vertical" flexItem />
          
          {/* Layout */}
          <ButtonGroup variant="outlined" size="small">
            <Button onClick={() => onLayout('TB')} startIcon={<FlowIcon />}>
              Layout ↓
            </Button>
            <Button onClick={() => onLayout('LR')} startIcon={<FlowIcon />}>
              Layout →
            </Button>
          </ButtonGroup>
          
          <Divider orientation="vertical" flexItem />
          
          {/* Import/Export */}
          <Button
            variant="outlined"
            size="small"
            startIcon={<CodeIcon />}
            onClick={exportToMermaid}
          >
            Mermaid
          </Button>
          
          <Button
            variant="outlined"
            size="small"
            startIcon={<DownloadIcon />}
            onClick={exportFlow}
          >
            Export
          </Button>
          
          <input
            accept=".json"
            style={{ display: 'none' }}
            id="import-file"
            type="file"
            onChange={importFlow}
          />
          <label htmlFor="import-file">
            <Button
              variant="outlined"
              size="small"
              component="span"
              startIcon={<UploadIcon />}
            >
              Import
            </Button>
          </label>
          
          <Divider orientation="vertical" flexItem />
          
          <Button
            variant="outlined"
            size="small"
            color="error"
            onClick={clearCanvas}
          >
            Clear
          </Button>
          
          {onSave && (
            <Button
              variant="contained"
              size="small"
              startIcon={<SaveIcon />}
              onClick={() => {
                exportToMermaid();
                setTimeout(() => onSave(mermaidCode), 100);
              }}
            >
              Save
            </Button>
          )}
          
          {onCancel && (
            <Button
              variant="outlined"
              size="small"
              onClick={onCancel}
            >
              Cancel
            </Button>
          )}
        </Toolbar>
      </Paper>

      {/* React Flow Canvas */}
      <ReactFlow
        ref={canvasRef}
        nodes={nodes}
        edges={edges}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        onConnect={onConnect}
        nodeTypes={nodeTypes}
        fitView
        attributionPosition="bottom-left"
        style={{ background: '#f8fafc' }}
      >
        <Controls position="bottom-right" />
        <MiniMap position="bottom-left" />
        <Background variant="dots" gap={20} size={1} />
      </ReactFlow>

      {/* Floating Action Buttons */}
      <Box sx={{ position: 'absolute', bottom: 16, right: 16, display: 'flex', flexDirection: 'column', gap: 1 }}>
        <Tooltip title="Fit View">
          <Fab size="small" onClick={fitView}>
            <FitScreenIcon />
          </Fab>
        </Tooltip>
        <Tooltip title="Auto Layout">
          <Fab size="small" color="primary" onClick={() => onLayout('TB')}>
            <AutoIcon />
          </Fab>
        </Tooltip>
      </Box>

      {/* Node Type Menu */}
      <Menu
        anchorEl={nodeMenu}
        open={Boolean(nodeMenu)}
        onClose={handleNodeMenuClose}
      >
        {Object.entries(nodeTemplates).map(([type, template]) => (
          <MenuItem key={type} onClick={() => handleAddNodeClick(type)}>
            <ListItemIcon>
              {template.data.icon ? <template.data.icon /> : <CircleIcon />}
            </ListItemIcon>
            <ListItemText primary={template.data.label} />
          </MenuItem>
        ))}
      </Menu>

      {/* Add Node Dialog */}
      <Dialog open={nodeDialog} onClose={() => setNodeDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add {selectedNodeType} Node</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Node Label"
            fullWidth
            variant="outlined"
            value={newNodeData.label}
            onChange={(e) => setNewNodeData(prev => ({ ...prev, label: e.target.value }))}
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            label="Description (Optional)"
            fullWidth
            multiline
            rows={3}
            variant="outlined"
            value={newNodeData.description}
            onChange={(e) => setNewNodeData(prev => ({ ...prev, description: e.target.value }))}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNodeDialog(false)}>Cancel</Button>
          <Button 
            onClick={() => addNode(selectedNodeType)}
            variant="contained"
            disabled={!newNodeData.label}
          >
            Add Node
          </Button>
        </DialogActions>
      </Dialog>

      {/* Mermaid Dialog */}
      <Dialog open={mermaidDialog} onClose={() => setMermaidDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>Mermaid Diagram</DialogTitle>
        <DialogContent>
          <TextField
            multiline
            rows={15}
            fullWidth
            variant="outlined"
            value={mermaidCode}
            onChange={(e) => setMermaidCode(e.target.value)}
            placeholder="Enter Mermaid flowchart code here..."
            sx={{ fontFamily: 'monospace' }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMermaidDialog(false)}>Close</Button>
          <Button 
            onClick={importFromMermaid}
            variant="contained"
            disabled={isLoading || !mermaidCode.trim()}
          >
            {isLoading ? 'Importing...' : 'Import'}
          </Button>
          {onSave && (
            <Button 
              onClick={() => {
                exportToMermaid();
                onSave(mermaidCode);
              }}
              variant="contained"
              color="primary"
            >
              Save & Export
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Notifications */}
      <Snackbar
        open={notification.open}
        autoHideDuration={3000}
        onClose={() => setNotification(prev => ({ ...prev, open: false }))}
      >
        <Alert 
          severity={notification.severity}
          onClose={() => setNotification(prev => ({ ...prev, open: false }))}
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

// Wrapper component with ReactFlowProvider
const FlowCanvas = (props) => {
  return (
    <ReactFlowProvider>
      <FlowCanvasComponent {...props} />
    </ReactFlowProvider>
  );
};

export default FlowCanvas;